import { apiDelete, apiGet, apiPost } from '@/shared/utils/requestBuilder';

import { lerDadosDaRespostaDoCore } from '@/shared/utils/respostaDoCore';
import type { ListaAutorizacoesAr } from './dto';
import { ErroAutorizacaoAr } from './erros';

const BASE = (processoArbitralId: string) =>
  `/arbitral/processos/${processoArbitralId}/ar/solicitacoes`;

/**
 * Quantos envios a leitura pede por vez. Explícito porque o default do servidor não é
 * contrato: um processo com mais requeridos do que ele perdia cards **em silêncio** —
 * a lista vinha truncada, `hasMore` era ignorado e o requerente autorizava um envio
 * sem saber que havia outro.
 */
const ENVIOS_POR_PAGINA = 50;

/** Teto de páginas por leitura. O GET aceita 120/min; um `hasMore` preso não pode consumi-lo. */
const MAX_PAGINAS = 10;

/** `replay: true` = a MESMA decisão repetida (retry do navegador), aceita sem gravar. */
export interface DecisaoAplicada {
  status: string;
  replay: boolean;
}

function extrair<T>(response: Response, mensagemDeErro: string): Promise<T> {
  return lerDadosDaRespostaDoCore<T>(
    response,
    mensagemDeErro,
    (falha) => new ErroAutorizacaoAr(falha.mensagem, falha.status, falha.codigo, falha.detalhes),
  );
}

/**
 * As seis portas do requerente sobre o envio de AR.
 *
 * **As decisões devolvem só `{ status, replay }`, e o hook relê a lista depois.** Não é
 * economia de código: o processo pode ter mais de um requerido, e o mesmo ato mexe em
 * mais de um card — pagar libera a postagem, a ciência digital cancela a carta do
 * vizinho, a varredura reconcilia o que estava parado. Costurar na tela qual outro card
 * mudou seria reimplementar, no navegador, a régua que o servidor já aplicou.
 */
export const autorizacaoArService = {
  /**
   * Lê TODAS as páginas de envios do processo antes de devolver.
   *
   * Um card faltando aqui não é "menos informação": é o requerente autorizando a
   * citação de um requerido sem saber que a do outro também está parada. O resumo
   * vem do servidor e vale para a coleção inteira, então o da última página é o que
   * fica — nunca a soma das páginas.
   */
  listar: async (processoArbitralId: string): Promise<ListaAutorizacoesAr> => {
    const paginas: ListaAutorizacoesAr[] = [];
    for (let page = 1; page <= MAX_PAGINAS; page += 1) {
      const response = await apiGet(
        `${BASE(processoArbitralId)}?page=${page}&limit=${ENVIOS_POR_PAGINA}`,
      );
      const pagina = await extrair<ListaAutorizacoesAr>(
        response,
        'Erro ao carregar os envios de AR',
      );
      paginas.push(pagina);
      if (!pagina.hasMore) break;
    }
    const ultima = paginas[paginas.length - 1];
    return {
      ...ultima,
      items: paginas.flatMap((p) => p.items ?? []),
      hasMore: false,
    };
  },

  /** Conveniado: autoriza sem checkout — o valor entra no fechamento do ciclo. */
  autorizarConvenio: (processoArbitralId: string, solicitacaoId: string) =>
    decidir(processoArbitralId, solicitacaoId, 'autorizacao'),

  devolverEndereco: (processoArbitralId: string, solicitacaoId: string, observacao: string) =>
    decidir(processoArbitralId, solicitacaoId, 'devolucao', { observacao }),

  recusar: (processoArbitralId: string, solicitacaoId: string, motivo: string | null) =>
    decidir(processoArbitralId, solicitacaoId, 'recusa', motivo ? { motivo } : {}),

  desfazerRecusa: async (
    processoArbitralId: string,
    solicitacaoId: string,
  ): Promise<DecisaoAplicada> => {
    const response = await apiDelete(`${BASE(processoArbitralId)}/${solicitacaoId}/recusa`);
    return extrair<DecisaoAplicada>(response, 'Não foi possível desfazer a recusa');
  },

  /** "Já paguei": consulta o gateway e aplica o pagamento, se já houver. */
  verificarPagamento: async (
    processoArbitralId: string,
    solicitacaoId: string,
  ): Promise<{ aplicado: string | null; desfecho: string }> => {
    const response = await apiPost(
      `${BASE(processoArbitralId)}/${solicitacaoId}/verificacao-pagamento`,
      {},
    );
    return extrair(response, 'Não foi possível verificar o pagamento');
  },
};

async function decidir(
  processoArbitralId: string,
  solicitacaoId: string,
  acao: 'autorizacao' | 'devolucao' | 'recusa',
  corpo: Record<string, string> = {},
): Promise<DecisaoAplicada> {
  const response = await apiPost(`${BASE(processoArbitralId)}/${solicitacaoId}/${acao}`, corpo);
  return extrair<DecisaoAplicada>(response, 'Não foi possível registrar sua decisão');
}
